import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, notification, Input, Space, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {colors} from '../theme';
import { useNavigate } from 'react-router-dom';
import { activesApi, Active } from '../apiCalls/activesApi';
import TableFiltersBar, { applyFiltersToColumns, useTableFilters } from '../components/TableFilters';
import PageTitle from '../components/PageTitle';

const ActivesList: React.FC = () => {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<Active[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const searchInput = useRef(null);
  const { filters, handleTableChange, clearAllFilters, removeFilterValue } = useTableFilters<Active>();

  useEffect(() => {
    fetchActives();
  }, []);

  const fetchActives = async () => {
    setLoading(true);
    const actives = await activesApi.getActives();
    setDataSource(actives);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    try {
      setDeleting(id);
      await activesApi.deleteActive(id);
      notification.success({
        message: 'Active Deleted',
        description: 'The active has been successfully removed.',
        duration: 3,
      });
      fetchActives();
    } catch (error: any) {
      notification.error({
        message: 'Error while deleting',
        description: error.message,
        duration: 5,
      });
    } finally {
      setDeleting(null);
    }
  };

  const showDeleteConfirm = (record: Active) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this active?',
      content: 'If this active is in use in any product or ingredient, it cannot be removed.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record.id);
      },
    });
  };

  // Funciones para el search en la columna "Name"
  const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
              ref={searchInput}
              placeholder={`Buscar ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(e) =>
                  setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
              style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
            >
              Buscar
            </Button>
            <Button
                onClick={() => clearFilters && handleReset(clearFilters)}
                size="small"
                style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
    ),
    filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value: any, record: Active) => {
      const recordValue = record[dataIndex as keyof Active];
      return recordValue
          ? recordValue.toString().toLowerCase().includes(String(value).toLowerCase())
          : false;
    },
  });

  const customNameSort = (a: Active, b: Active): number => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    // Function to determine if the first character is a letter
    const startsWithLetter = (name: string) => /^[a-z]/i.test(name);

    const aStartsWithLetter = startsWithLetter(nameA);
    const bStartsWithLetter = startsWithLetter(nameB);

    // If a starts with a letter and b doesn't, a must come first (ascending)
    if (aStartsWithLetter && !bStartsWithLetter) return -1;
    // If a does not start with a letter and b does, a must come after
    if (!aStartsWithLetter && bStartsWithLetter) return 1;

    // If both start the same way, they are sorted alphabetically
    return nameA.localeCompare(nameB);
  };

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'left' as const,
      width: 100,
      align: 'center' as const,
      render: (_: any, record: Active) => (
          <Space size={0}>
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                style={{color: colors.primary}}
                icon={<EditOutlined />}
                onClick={() => navigate(`/actives/${record.id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                danger
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => showDeleteConfirm(record)}
                loading={deleting === record.id}
              />
            </Tooltip>
          </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: customNameSort,
      defaultSortOrder: 'ascend' as const,
      ...getColumnSearchProps('name'),
    },
    {
      title: 'VRN',
      dataIndex: 'vrn',
      key: 'vrn',
      render: (value: number | null) => (value !== null ? value : 'N/A'),
    },
    {
      title: 'VRN Unit',
      dataIndex: 'vrnUnit',
      key: 'vrnUnit',
    },
    {
      title: 'I.U. Claim',
      dataIndex: 'iuClaim',
      key: 'iuClaim',
      render: (value: number | null) => (value !== null ? value : 'N/A'),
    },
    {
      title: 'UL',
      dataIndex: 'ul',
      key: 'ul',
      render: (value: number | null) => (value !== null ? value : 'N/A'),
    },
    {
      title: 'UL Unit',
      dataIndex: 'ulUnit',
      key: 'ulUnit',
    },
  ];
  const tableColumns = applyFiltersToColumns(columns, filters);

  return (
      <div style={{padding: '24px 32px'}}>
        <PageTitle title="List of Actives" />
        <Button
            type="primary"
            onClick={() => navigate('/actives/new')}
            style={{marginBottom: 16}}
        >
          Add New Active
        </Button>
        <TableFiltersBar
          columns={tableColumns}
          filters={filters}
          onClearAll={clearAllFilters}
          onRemove={removeFilterValue}
        />
        <Table
            dataSource={dataSource}
            columns={tableColumns}
            rowKey="id"
            loading={loading}
            pagination={{defaultPageSize: 50, pageSizeOptions: [10, 25, 50]}}
            scroll={{x: 'max-content', y: 'calc(100vh - 300px)'}}
            onChange={handleTableChange}
        />
      </div>
  );
};

export default ActivesList;
